import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import Swal from "sweetalert2/dist/sweetalert2.js";
import RkHeader from '../../../GeneralComponents/RkHeader';
import Preloader from '../../../GeneralComponents/LoadingComponen';
import RiskPagination from '../../risk/RiskPagination';
import HistoryModal from './HistoryModal';

// Papelera del tenant: registros (surveys) eliminados de todos los formatos,
// solo visible para el Administrador Sistema (gate en el backend). Versión
// por-tenant del Recovery del backoffice, en sistema rk-.
const Index = (props) => {
    const token = document.querySelector("[name='csrf-token']").content;

    const [rows, setRows] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [loading, setLoading] = useState(true);
    const [formats, setFormats] = useState([]);
    const [formatId, setFormatId] = useState("");
    const [userDeleted, setUserDeleted] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [restoringId, setRestoringId] = useState(null);
    const [historyRow, setHistoryRow] = useState(null);

    const fmtDate = (d) => {
        if (!d) return "—";
        const date = new Date(d);
        const fecha = date.toLocaleDateString("es", { day: "numeric", month: "short", year: "numeric" });
        const hora = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
        return `${fecha}, ${hora}`;
    };

    const loadData = useCallback((p, opts = {}) => {
        const fId = opts.formatId !== undefined ? opts.formatId : formatId;
        const per = opts.perPage !== undefined ? opts.perPage : perPage;
        setLoading(true);
        const params = new URLSearchParams({ page: p, per_page: per });
        if (fId) params.append("format_id", fId);
        if (userDeleted) params.append("user_deleted", userDeleted);
        if (dateFrom) params.append("date_deleted_from", dateFrom);
        if (dateTo) params.append("date_deleted_to", dateTo);

        fetch(`${props.data_url}?${params.toString()}`, {
            headers: { Accept: 'application/json', "X-CSRF-Token": token },
            credentials: 'same-origin',
        })
            .then(r => r.json())
            .then(data => {
                setRows(data.data || []);
                setTotalData(data.total_data || 0);
                setPage(p);
            })
            .catch(() => { setRows([]); setTotalData(0); })
            .finally(() => setLoading(false));
    }, [formatId, perPage, userDeleted, dateFrom, dateTo, props.data_url, token]);

    const loadFormats = () => {
        fetch(props.formats_url, {
            headers: { Accept: 'application/json', "X-CSRF-Token": token },
            credentials: 'same-origin',
        })
            .then(r => r.json())
            .then(data => setFormats(data.data || []))
            .catch(() => setFormats([]));
    };

    useEffect(() => { loadFormats(); loadData(1); }, []);

    const clearFilters = () => {
        setFormatId(""); setUserDeleted(""); setDateFrom(""); setDateTo("");
        setLoading(true);
        fetch(`${props.data_url}?page=1&per_page=${perPage}`, {
            headers: { Accept: 'application/json', "X-CSRF-Token": token },
            credentials: 'same-origin',
        })
            .then(r => r.json())
            .then(data => { setRows(data.data || []); setTotalData(data.total_data || 0); setPage(1); })
            .finally(() => setLoading(false));
    };

    const handleRestore = (row) => {
        Swal.fire({
            title: "¿Restaurar registro?",
            text: `Se restaurará el registro ${row.code} del formato "${row.format_name}"`,
            type: "warning",
            showCancelButton: true,
            confirmButtonColor: "#0061fe",
            cancelButtonColor: "#d33",
            confirmButtonText: "Sí, restaurar",
            cancelButtonText: "Cancelar",
        }).then(result => {
            if (!result.value) return;
            setRestoringId(row.id);
            fetch(`${props.restore_url_base}/${row.id}/restore`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', "X-CSRF-Token": token, Accept: 'application/json' },
                credentials: 'same-origin',
            })
                .then(r => r.json().then(j => ({ ok: r.ok, j })))
                .then(({ ok, j }) => {
                    if (ok && j.type === "success") {
                        setRows(prev => prev.filter(x => x.id !== row.id));
                        setTotalData(prev => Math.max(0, prev - 1));
                        setFormats(prev => prev
                            .map(f => (f.id === row.format_id ? { ...f, count: f.count - 1 } : f))
                            .filter(f => f.count > 0));
                        if (j.warning) {
                            // Colisión de consecutivo: el usuario debe leer el código nuevo.
                            Swal.fire({ type: "info", title: "Registro restaurado", text: j.warning, confirmButtonText: "Entendido" });
                        } else {
                            Swal.fire({ position: "center", type: "success", title: j.success || "Registro restaurado", showConfirmButton: false, timer: 1500 });
                        }
                    } else {
                        Swal.fire({ type: "error", title: "No se pudo restaurar", text: (j && j.success) || "Inténtalo de nuevo." });
                    }
                })
                .catch(() => Swal.fire({ type: "error", title: "No se pudo restaurar", text: "Error de conexión." }))
                .finally(() => setRestoringId(null));
        });
    };

    const totalDeleted = formats.reduce((a, f) => a + f.count, 0);
    const formatOptions = [
        { value: "", label: `Todos los formatos (${totalDeleted})` },
        ...formats.map(f => ({ value: f.id, label: `${f.name} (${f.count})` })),
    ];
    const hasFilters = formatId || userDeleted || dateFrom || dateTo;

    return (
        <div className="rk-wrap">
            <RkHeader
                icon="fas fa-recycle"
                title="Registros eliminados"
                subtitle="Consulta y restaura los registros eliminados de los formatos de tu empresa"
                showFilter
                titleFilter={showFilters ? "Cerrar filtros" : "Filtros"}
                onClickFilter={() => setShowFilters(!showFilters)}
            />

            {showFilters && (
                <div className="rk-filter">
                    <div className="rk-filter__head"><i className="fas fa-filter"></i> Filtros</div>
                    <div className="rk-filter__grid">
                        <div className="rk-field">
                            <label className="rk-label">Formato</label>
                            <Select
                                classNamePrefix="select"
                                options={formatOptions}
                                value={formatOptions.find(o => o.value === formatId) || formatOptions[0]}
                                onChange={o => { setFormatId(o.value); loadData(1, { formatId: o.value }); }}
                                placeholder="Todos los formatos"
                            />
                        </div>
                        <div className="rk-field">
                            <label className="rk-label">Eliminado por</label>
                            <input
                                type="text"
                                className="rk-input"
                                value={userDeleted}
                                placeholder="Nombre de usuario"
                                onChange={e => setUserDeleted(e.target.value)}
                                onKeyPress={e => { if (e.key === 'Enter') loadData(1); }}
                            />
                        </div>
                        <div className="rk-field">
                            <label className="rk-label">Eliminado desde</label>
                            <input type="date" className="rk-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                        </div>
                        <div className="rk-field">
                            <label className="rk-label">Eliminado hasta</label>
                            <input type="date" className="rk-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                        </div>
                    </div>
                    <div className="rk-filter__actions">
                        <button className="rk-btn rk-btn--ghost" onClick={clearFilters}>
                            <i className="fas fa-times"></i> Limpiar
                        </button>
                        <button className="rk-btn rk-btn--primary" onClick={() => loadData(1)}>
                            <i className="fas fa-search"></i> Aplicar
                        </button>
                    </div>
                </div>
            )}

            {loading ? <Preloader /> : (
                <div className="rk-table-card">
                    {rows.length ? (
                        <React.Fragment>
                            <div className="rk-table-scroll">
                                <table className="rk-table">
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Formato</th>
                                            <th>Eliminado por</th>
                                            <th>Fecha eliminación</th>
                                            <th>Creado</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(row => (
                                            <tr key={row.id}>
                                                <td className="rk-cell-primary">{row.code}</td>
                                                <td>{row.format_name}</td>
                                                <td>{row.user_name || "N/A"}</td>
                                                <td className="rk-cell-muted">{fmtDate(row.date_deleted)}</td>
                                                <td className="rk-cell-muted">{fmtDate(row.created_at)}</td>
                                                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                                                    <button className="rk-icon-btn" title="Historial de cambios"
                                                        onClick={() => setHistoryRow(row)} style={{ marginRight: 8 }}>
                                                        <i className="fas fa-history"></i>
                                                    </button>
                                                    <button className="rk-btn rk-btn--ghost" disabled={restoringId === row.id}
                                                        onClick={() => handleRestore(row)}>
                                                        {restoringId === row.id
                                                            ? <React.Fragment><i className="fas fa-spinner fa-spin"></i> Restaurando…</React.Fragment>
                                                            : <React.Fragment><i className="fas fa-undo"></i> Restaurar</React.Fragment>}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <RiskPagination
                                page={page}
                                perPage={perPage}
                                total={totalData}
                                perPageOptions={[25, 50, 100]}
                                onPage={p => loadData(p)}
                                onPerPage={n => { setPerPage(n); loadData(1, { perPage: n }); }}
                            />
                        </React.Fragment>
                    ) : (
                        <div className="rk-empty">
                            <i className="fas fa-recycle"></i>
                            <p>{hasFilters
                                ? "No hay registros eliminados con los filtros aplicados"
                                : "No hay registros eliminados"}</p>
                        </div>
                    )}
                </div>
            )}

            {historyRow && (
                <HistoryModal survey={historyRow} onClose={() => setHistoryRow(null)} />
            )}
        </div>
    );
};

export default Index;
